import { getFleetDetailsLink, updateScopeToFleet } from "components/fleet/util/fleetsNavigationUtils";
import { RESOURCE_CLASS, RESOURCE_TYPE } from "modules/fleet-management/constants";
import { uuidv4 } from "utils/cryptoRandomString";
import {
    CLUSTER_INFO_SUBJECT,
    SM_SUPPORTED_OBJECTS,
    launchSystemManagerPage,
    notifySystemManagerObservable
} from "utils/systemManagerNavigationHelper";

const SEARCH_PAGE_NAME = "GLOBAL-SEARCH";

const SYSTEM_MANAGER_TYPES = {
    System: SM_SUPPORTED_OBJECTS.SYSTEM,
    Node: SM_SUPPORTED_OBJECTS.SYSTEM,
    "Storage VM": SM_SUPPORTED_OBJECTS.SVM,
    Volume: SM_SUPPORTED_OBJECTS.VOLUME,
    Aggregate: SM_SUPPORTED_OBJECTS.LOCAL_TIER
};

const systemManagerType = result => {
    if (result.objectType === "LUN") {
        return result.clusterPersonality === "ASA_R2" ? SM_SUPPORTED_OBJECTS.LUN_ASA_R2 : SM_SUPPORTED_OBJECTS.LUN;
    }
    return SYSTEM_MANAGER_TYPES[result.objectType] || null;
};

// A node has no System Manager page of its own. Inventory opens the parent system overview.
const systemManagerTarget = result => {
    const objectType = systemManagerType(result);
    if (!objectType) {
        return null;
    }
    if (result.objectType === "Node") {
        if (!result.clusterId && !result.workingEnvironmentId) {
            return null;
        }
        return { objectType, objectId: result.clusterId || result.id, clusterId: result.clusterId };
    }
    const clusterId = result.objectType === "System" ? result.id : result.clusterId;
    return result.id ? { objectType, objectId: result.id, clusterId } : null;
};

let activeNavigation = null;

export const cancelActiveNavigation = () => {
    activeNavigation?.unsubscribe();
    activeNavigation = null;
};

const openSystemManager = ({ history, agentId, result }) => {
    const target = systemManagerTarget(result);
    if (!target) {
        console.error("Global search could not open this console object", result?.id, result?.objectType);
        return;
    }

    if (result.workingEnvironmentId && agentId) {
        launchSystemManagerPage({
            history,
            pageName: SEARCH_PAGE_NAME,
            navigationId: uuidv4(),
            tenancyAlertInfo: {
                objectType: target.objectType,
                objectId: target.objectId,
                weId: result.workingEnvironmentId,
                resourceType: RESOURCE_TYPE,
                resourceClass: RESOURCE_CLASS,
                legacyAgentId: agentId
            }
        });
        return;
    }

    if (!target.clusterId) {
        console.error("Global search could not open this console object", result?.id, result?.objectType);
        return;
    }

    cancelActiveNavigation();
    const finishNavigation = () => {
        subscription.unsubscribe();
        if (activeNavigation === subscription) {
            activeNavigation = null;
        }
    };
    const subscription = CLUSTER_INFO_SUBJECT.subscribe(event => {
        if (event.type === "error") {
            if (event.objectId !== target.objectId) {
                return;
            }
            console.error("Failed to resolve System Manager target from global search", event);
            finishNavigation();
            return;
        }
        if (!event.resourceInfoAvailable || event.tenancyAlertInfo?.objectId !== target.objectId) {
            return;
        }
        finishNavigation();
        launchSystemManagerPage({
            history,
            pageName: SEARCH_PAGE_NAME,
            navigationId: uuidv4(),
            tenancyAlertInfo: event.tenancyAlertInfo
        });
    });
    activeNavigation = subscription;
    notifySystemManagerObservable({
        clusterUuid: target.clusterId,
        objectType: target.objectType,
        objectId: target.objectId,
        originId: agentId
    });
};

export const navigateToConsoleObject = ({ history, dispatch, agentId, result }) => {
    if (result.objectType === "Fleet" && result.fleetId) {
        updateScopeToFleet(dispatch, result.fleetId);
        history.push(getFleetDetailsLink(result.fleetId));
        return;
    }
    openSystemManager({ history, agentId, result });
};
